import * as toastsActions from '@zola-helpers/client/dist/es/redux/toasts/toastsActions';
import ApiService from '@zola-helpers/client/dist/es/http/api';
import type { CamelCasedPropertiesDeep, SetRequired } from 'type-fest';

import type {
  ApplyPromo,
  BraintreeTokenViewResponse,
  CheckoutCartViewResponse,
  CheckoutResponse,
  CheckoutUpdateQuantityViewResponse,
  CheckoutViewResponse,
  ItemQuantity,
  RemoveItems,
  Submit,
  UpdateTotals,
  ValidateAddress,
  WAddCartItemRequest,
  WCartItemDetailView,
  WCartView,
  WGetCartViewResponse,
} from '@zola/svc-web-api-ts-client';
import { ConvertWebApiDeclaredToActual } from '@zola-helpers/client/dist/es/@types/svc-web-api';
import LogService from '@zola-helpers/client/dist/es/util/logService';
import { setShippingRequestAttributes } from '@/util/shippingHelper';
import * as ActionType from '../types/CartActionTypes';
import { AppThunk } from '../types';
import { CreditCardView } from '../types/CartActionTypes';

type CheckoutErrorPayload = {
  request: CheckoutRequest;
  requestResponse: unknown;
};
export const logCheckoutError = (
  errorMessage: string,
  payload: CheckoutErrorPayload,
  cartId: string | undefined = undefined
): string => {
  const finalPayload = { ...payload, cartId, checkoutRedesign: true };
  return LogService.log(errorMessage, finalPayload);
};

const requestSubmitCheckout = () => ({
  type: ActionType.REQUEST_SUBMIT_CHECKOUT,
});

const receiveSubmitCheckout = (
  response: CamelCasedPropertiesDeep<ConvertWebApiDeclaredToActual<CheckoutResponse>>
) => ({
  type: ActionType.RECEIVE_SUBMIT_CHECKOUT,
  payload: response.data,
});

type WithoutBaseRequest<T> = Omit<T, 'user_agent' | 'ip_address' | 'user_id' | 'store'>;
export type CheckoutRequest = CamelCasedPropertiesDeep<
  Omit<
    WithoutBaseRequest<Submit>,
    'guest' | 'impact_radius_click_id' | 'order_key' | 'post_auth_click_id'
  >
>;

export const submitCheckout =
  (body: CheckoutRequest): AppThunk<Promise<ReturnType<typeof receiveSubmitCheckout>>> =>
  (dispatch, getState) => {
    dispatch(requestSubmitCheckout());

    return ApiService.post<
      CamelCasedPropertiesDeep<ConvertWebApiDeclaredToActual<CheckoutResponse>>,
      CheckoutRequest
    >('/web-registry-api/v1/checkout', body)
      .then((json) => dispatch(receiveSubmitCheckout(json)))
      .catch((error) => {
        const { cart } = getState();
        const cartId = cart && cart.checkoutData ? cart.checkoutData.cartId : undefined;
        const errorPayload = {
          request: body,
          requestResponse: error.response,
        };
        const errorId = logCheckoutError(error.message, errorPayload, cartId);
        dispatch(toastsActions.negative({ headline: `${error.message} (Support ID: ${errorId})` }));
        throw error;
      });
  };

type UpdateTotalsRequest = SetRequired<
  CamelCasedPropertiesDeep<WithoutBaseRequest<UpdateTotals>>,
  'registryId' | 'zipCode' | 'isExpedited' | 'isRushed'
>;

export const receiveUpdatedCart = (
  response: CamelCasedPropertiesDeep<CheckoutCartViewResponse['data']>
) => ({
  type: ActionType.RECEIVE_UPDATED_CART,
  payload: response,
});

/**
 * Updates cart totals based on zipcode and delivery method
 */
export const updateTotals =
  (body: UpdateTotalsRequest): AppThunk<Promise<ReturnType<typeof receiveUpdatedCart>>> =>
  (dispatch) => {
    return ApiService.put<CamelCasedPropertiesDeep<CheckoutCartViewResponse>>(
      `/web-registry-api/v1/checkout/totals`,
      body
    ).then((json) => dispatch(receiveUpdatedCart(json.data)));
  };

const receiveDeletedItems = (items: CamelCasedPropertiesDeep<WCartItemDetailView>[]) => ({
  type: ActionType.RECEIVE_DELETED_ITEMS,
  payload: items,
});

const requestCart = () => ({
  type: ActionType.REQUEST_CART,
});

const receiveCart = (response: CamelCasedPropertiesDeep<WGetCartViewResponse>) => ({
  type: ActionType.RECEIVE_CART,
  payload: response.data,
});

const removeItems =
  (itemIds: string[]): AppThunk<Promise<ReturnType<typeof receiveCart>>> =>
  (dispatch) => {
    dispatch(requestCart());
    return ApiService.post<CamelCasedPropertiesDeep<WGetCartViewResponse>>(
      `/web-registry-api/v1/cart/items`,
      { itemIds }
    ).then((json) => dispatch(receiveCart(json)));
  };

export const getCart = (): AppThunk<Promise<ReturnType<typeof receiveCart>>> => (dispatch) => {
  dispatch(requestCart());

  return ApiService.get<CamelCasedPropertiesDeep<WGetCartViewResponse>>(
    '/web-registry-api/v1/cart'
  ).then((json) => {
    if (json?.data?.items && json.data.items.length > 0) {
      // if cart has out of stock items, delete them and return updated result
      const OUT_OF_STOCK = 'out of stock';
      const itemsToDelete = json.data.items.filter(
        (item) =>
          item.availability && item.availability?.stockStatus?.toLowerCase() === OUT_OF_STOCK
      );
      if (itemsToDelete.length > 0) {
        dispatch(receiveDeletedItems(itemsToDelete));
        const ids = itemsToDelete.map((item) => item.id) as string[];
        return dispatch(removeItems(ids));
      }
    }
    return dispatch(receiveCart(json));
  });
};

const requestInitialCartDetails = () => ({
  type: ActionType.REQUEST_INITIAL_CART_DETAILS,
});

const receiveInitialCartDetails = (response: CamelCasedPropertiesDeep<CheckoutViewResponse>) => ({
  type: ActionType.RECEIVE_INITIAL_CART_DETAILS,
  payload: response.data,
});

export const getInitialCartDetails =
  (): AppThunk<Promise<ReturnType<typeof receiveInitialCartDetails>>> => (dispatch) => {
    dispatch(requestInitialCartDetails());

    return ApiService.get<CamelCasedPropertiesDeep<CheckoutViewResponse>>(
      '/web-registry-api/v1/checkout'
    ).then((json) => dispatch(receiveInitialCartDetails(json)));
  };

const requestShippingValidation = () => ({
  type: ActionType.REQUEST_SHIPPING_VALIDATION,
});

const receiveShippingValidation = (
  response: CamelCasedPropertiesDeep<CheckoutResponse>,
  allowBypass: boolean
) => ({
  type: ActionType.RECEIVE_SHIPPING_VALIDATION,
  payload: {
    ...response,
    allowBypass,
  },
});
type ValidateAddressRequest = CamelCasedPropertiesDeep<WithoutBaseRequest<ValidateAddress>>;

export const checkShippingAddress =
  (
    body: ValidateAddressRequest,
    allowBypass: boolean
  ): AppThunk<Promise<ReturnType<typeof receiveShippingValidation>>> =>
  (dispatch) => {
    dispatch(requestShippingValidation());

    return ApiService.post<CamelCasedPropertiesDeep<CheckoutResponse>>(
      '/web-registry-api/v1/checkout/validate-address',
      body
    ).then((json) => {
      if (json.data && json.data.userMessage) {
        dispatch(receiveShippingValidation(json, allowBypass));
        throw Error(json.data.userMessage);
      }
      return dispatch(receiveShippingValidation(json, allowBypass));
    });
  };

type CreditCardResponse = {
  data: CreditCardView[];
};

const requestCreditCard = () => ({
  type: ActionType.REQUEST_CREDIT_CARD,
});

const receiveCreditCard = (response: CreditCardResponse) => ({
  type: ActionType.RECEIVE_CREDIT_CARD,
  payload: response.data,
});

export const getDefaultCreditCard =
  (): AppThunk<Promise<ReturnType<typeof receiveCreditCard>>> => (dispatch) => {
    dispatch(requestCreditCard());
    // This is a web-zola route (CreditCardView)
    return ApiService.get<CreditCardResponse>('/api/v0/cart/credit-cards/get-credit-cards').then(
      (json) => dispatch(receiveCreditCard(json))
    );
  };

const requestBraintreeToken = () => ({
  type: ActionType.REQUEST_BRAINTREE_TOKEN,
});

const receiveBraintreeToken = (braintreeToken: string | undefined) => ({
  type: ActionType.RECEIVE_BRAINTREE_TOKEN,
  payload: braintreeToken,
});

export const getBraintreeToken =
  (): AppThunk<Promise<ReturnType<typeof receiveBraintreeToken>>> => (dispatch) => {
    dispatch(requestBraintreeToken());

    return ApiService.get<CamelCasedPropertiesDeep<BraintreeTokenViewResponse>>(
      '/web-registry-api/v1/checkout/braintree/token'
    ).then((json) => dispatch(receiveBraintreeToken(json.data?.braintreeToken)));
  };

const requestRemovePromo = () => ({
  type: ActionType.REQUEST_REMOVE_PROMO,
});

export const removePromo =
  (
    isExpedited: boolean,
    isRushed: boolean,
    promoCode: string,
    zipCode: string,
    registryId: string
  ): AppThunk<Promise<ReturnType<typeof receiveUpdatedCart>>> =>
  (dispatch) => {
    dispatch(requestRemovePromo());

    const baseReqAttributes = {
      ...setShippingRequestAttributes(isExpedited, isRushed),
      promoCode,
    };

    return ApiService.post<
      CamelCasedPropertiesDeep<CheckoutCartViewResponse>,
      CamelCasedPropertiesDeep<WithoutBaseRequest<UpdateTotals>>
    >(
      '/web-registry-api/v1/checkout/remove-promo',
      !registryId ? { ...baseReqAttributes, zipCode } : { ...baseReqAttributes, registryId }
    )
      .then((json) => dispatch(receiveUpdatedCart(json.data)))
      .catch((response) => {
        const error =
          response.response && response.response.error && response.response.error.message;
        dispatch(toastsActions.negative({ headline: error.message }));
        throw error || response;
      });
  };

const requestApplyPromo = () => ({
  type: ActionType.REQUEST_APPLY_PROMO,
});

export const applyPromo =
  (promoCode: string): AppThunk<Promise<ReturnType<typeof receiveUpdatedCart>>> =>
  (dispatch) => {
    dispatch(requestApplyPromo());

    return ApiService.post<
      CamelCasedPropertiesDeep<CheckoutCartViewResponse>,
      CamelCasedPropertiesDeep<Pick<ApplyPromo, 'promo_code'>>
    >('/web-registry-api/v1/checkout/apply-promo', { promoCode })
      .then((json) => {
        return dispatch(receiveUpdatedCart(json.data));
      })
      .catch((response) => {
        throw response?.response?.error?.trigger || response;
      });
  };

const requestRemoveCredits = () => ({
  type: ActionType.REQUEST_REMOVE_CREDITS,
});

export const removeCredits =
  (
    isExpedited: boolean,
    isRushed: boolean,
    zipCode: string,
    registryId: string
  ): AppThunk<Promise<ReturnType<typeof receiveUpdatedCart>>> =>
  (dispatch) => {
    dispatch(requestRemoveCredits());

    // Note: as of the TS conversion time, none of these attributes are actually used on the backend to
    // find the cart and remove the credits.  The cart is found by the user's session / user id
    const body = {
      ...setShippingRequestAttributes(isExpedited, isRushed),
      zipCode,
      registryId,
    };

    return ApiService.post<CamelCasedPropertiesDeep<CheckoutCartViewResponse>, UpdateTotalsRequest>(
      '/web-registry-api/v1/checkout/remove-credit',
      body
    )
      .then((json) => dispatch(receiveUpdatedCart(json.data)))
      .catch((response) => {
        const error = response.response && response.response.error && response.response.error;
        dispatch(toastsActions.negative({ headline: error.message }));
        throw error || response;
      });
  };

function requestApplyCredits() {
  return {
    type: ActionType.REQUEST_APPLY_CREDITS,
  };
}

export const applyCredits =
  (
    isExpedited: boolean,
    isRushed: boolean,
    zipCode: string,
    registryId: string
  ): AppThunk<Promise<ReturnType<typeof receiveUpdatedCart>>> =>
  (dispatch) => {
    dispatch(requestApplyCredits());

    const baseReqAttributes = setShippingRequestAttributes(isExpedited, isRushed);

    return ApiService.post<CamelCasedPropertiesDeep<CheckoutCartViewResponse>>(
      '/web-registry-api/v1/checkout/apply-credit',
      !registryId ? { ...baseReqAttributes, zipCode } : { ...baseReqAttributes, registryId }
    )
      .then((json) => dispatch(receiveUpdatedCart(json.data)))
      .catch((response) => {
        const error = response.response && response.response.error && response.response.error;
        dispatch(toastsActions.negative({ headline: error.message }));
        throw error || response;
      });
  };

const requestRemoveCreditCard = () => ({
  type: ActionType.REQUEST_REMOVE_CREDIT_CARD,
});

const receiveRemoveCreditCard = (response: { data: null }) => ({
  type: ActionType.RECEIVE_REMOVE_CREDIT_CARD,
  payload: response.data,
});

export const removeCreditCard =
  (cardId: string): AppThunk<Promise<ReturnType<typeof receiveRemoveCreditCard>>> =>
  (dispatch) => {
    dispatch(requestRemoveCreditCard());
    return ApiService.post<{ data: null }>('/api/v0/cart/credit-cards/remove-credit-card', cardId)
      .then((json) => dispatch(receiveRemoveCreditCard(json)))
      .catch((response) => {
        const error = response.response && response.response.error && response.response.error;
        dispatch(toastsActions.negative({ headline: error.message }));
        throw error || response;
      });
  };

const requestUpdatedCart = () => ({
  type: ActionType.REQUEST_UPDATED_CART,
});

export const updateItemQuantity =
  (
    id: string,
    quantity: number,
    isExpedited: boolean,
    isRushed: boolean,
    zipCode: string,
    registryId: string
  ): AppThunk<Promise<ReturnType<typeof receiveUpdatedCart>>> =>
  (dispatch) => {
    dispatch(requestUpdatedCart());

    const baseReqAttributes = {
      ...setShippingRequestAttributes(isExpedited, isRushed),
      quantity,
    };

    return ApiService.put<
      CamelCasedPropertiesDeep<CheckoutUpdateQuantityViewResponse>,
      CamelCasedPropertiesDeep<WithoutBaseRequest<ItemQuantity>>
    >(
      `/web-registry-api/v1/checkout/cart/item/id/${id}`,
      !registryId ? { ...baseReqAttributes, zipCode } : { ...baseReqAttributes, registryId }
    )
      .then((json) => {
        if (json && json.data?.errorMessage) {
          dispatch(toastsActions.negative({ headline: json.data.errorMessage }));
        }
        return dispatch(receiveUpdatedCart(json.data!.cart));
      })
      .catch((error) => {
        dispatch(toastsActions.negative({ headline: error.message }));
        throw error;
      });
  };

export const removeSuiteItems =
  (
    itemIds: string[],
    isExpedited: boolean,
    isRushed: boolean,
    zipCode: string,
    registryId: string
  ): AppThunk<Promise<ReturnType<typeof receiveUpdatedCart>>> =>
  (dispatch) => {
    dispatch(requestUpdatedCart());

    const body = {
      itemIds,
      ...setShippingRequestAttributes(isExpedited, isRushed),
      zipCode: !registryId ? zipCode : undefined,
      registryId: registryId || undefined,
    };

    return ApiService.post<
      CamelCasedPropertiesDeep<CheckoutCartViewResponse>,
      CamelCasedPropertiesDeep<WithoutBaseRequest<RemoveItems>>
    >(`/web-registry-api/v1/checkout/cart/items`, body).then((json) => {
      return dispatch(receiveUpdatedCart(json.data));
    });
  };

export const removeItem =
  (
    id: string,
    isExpedited: boolean,
    isRushed: boolean,
    zipCode: string,
    registryId: string
  ): AppThunk<Promise<ReturnType<typeof receiveUpdatedCart>>> =>
  (dispatch) => {
    dispatch(requestUpdatedCart());

    const baseReqAttributes = setShippingRequestAttributes(isExpedited, isRushed);

    return ApiService.post<
      CamelCasedPropertiesDeep<CheckoutCartViewResponse>,
      CamelCasedPropertiesDeep<WithoutBaseRequest<UpdateTotals>>
    >(
      `/web-registry-api/v1/checkout/cart/item/id/${id}`,
      !registryId ? { ...baseReqAttributes, zipCode } : { ...baseReqAttributes, registryId }
    ).then((json) => dispatch(receiveUpdatedCart(json.data)));
  };

const requestAddItemToCart = () => ({
  type: ActionType.ADD_ITEM_TO_CART,
});

const receiveAddItemToCart = (json: WCartView) => ({
  type: ActionType.ADDED_ITEM_TO_CART,
  payload: json,
});

export const addItemToCart =
  (
    body: CamelCasedPropertiesDeep<WithoutBaseRequest<WAddCartItemRequest>>
  ): AppThunk<Promise<ReturnType<typeof receiveAddItemToCart>>> =>
  (dispatch) => {
    dispatch(requestAddItemToCart());

    // Note, this does not camel case the response
    // Also note: the server sends .data not the response, which is different
    // from most of the other actions in this file
    return ApiService.post<
      WCartView,
      CamelCasedPropertiesDeep<WithoutBaseRequest<WAddCartItemRequest>>
    >('/web-registry-api/v1/cart/item', body)
      .then((json) => dispatch(receiveAddItemToCart(json)))
      .catch((response) => {
        const error = response.response && response.response.error && response.response.error;
        dispatch(toastsActions.negative({ headline: error.message }));
        throw error || response;
      });
  };
